/**
 * Cookie and tracking consent, and what a refusal actually stops.
 *
 * LAUNCH_PLAN task 92. The plan's own sentence is the requirement: *"a banner that does not
 * suppress collection is worse than none"* — worse because it converts a legal obligation into a
 * claim, and a claim is what a regulator tests.
 *
 * So this module is the ONE place that decides, and every sender asks it:
 *
 *   `monitoring.trackEvent`  product-usage events
 *   `monitoring.recordMetric` performance metrics (LCP/FID/CLS)
 *   `monitoring.sendErrorToService` → Sentry, a third party, over the network
 *
 * THE THREE CATEGORIES, and why there are only three.
 *
 *   essential   never gated, never asked about: sign-in, the CSRF nonce, rate limiting, the
 *               screen-time clock. Asking permission for these would be asking whether the
 *               product may work.
 *   functional  a remembered preference in `localStorage` — the collapsed panel, the chosen
 *               tab, an unsent draft. It never leaves the device, so refusing it costs the
 *               viewer convenience and protects nobody; it is offered because some
 *               jurisdictions treat any non-essential storage as consent-requiring.
 *   analytics   anything that leaves the device for a third party. Usage events, performance
 *               metrics AND error telemetry are one category deliberately: they go to the same
 *               kinds of processor, they carry an IP address by virtue of being an HTTP request,
 *               and a viewer who says no to "tracking" does not mean "except the crash
 *               reporter". `docs/DECISIONS.md` § 11 records that choice and what would reverse
 *               it.
 *
 * WHICH WAY IT FAILS. Unset means DENIED, for every non-essential category. A first visit sends
 * nothing; a viewer who closes the banner without choosing sends nothing; a browser with
 * `localStorage` blocked or cleared sends nothing. That is the opposite of the default this
 * codebase keeps getting wrong (`data.status || 'active'`, `FEATURE_FLAGS['toString']`), and it
 * is the only direction that is safe here: the cost of failing closed is a missing metric, and
 * the cost of failing open is collecting a child's data without permission.
 *
 * A CHILD CANNOT CONSENT. Under COPPA the operator needs the PARENT's permission, and a
 * ten-year-old clicking Accept is not that. `lockForChildAccount()` forces every non-essential
 * category off and makes the choice unavailable, so a covered account cannot grant what it
 * cannot grant — regardless of what a previous visitor to that browser chose.
 */

import { logger } from './logger';

export type ConsentCategory = 'functional' | 'analytics';

export interface TrackingConsent {
  functional: boolean;
  analytics: boolean;
  /** Whether the viewer has answered at all. `false` means "not yet asked or not yet answered". */
  decided: boolean;
}

/** Everything off, nothing answered. The state a first visit is in, and a refusal. */
export const DENIED: TrackingConsent = { functional: false, analytics: false, decided: false };

const STORAGE_KEY = 'nsgs.trackingConsent.v1';

/**
 * Forced off for a COPPA-covered account, regardless of what is stored.
 *
 * Module-level rather than part of the stored record, because it is not the viewer's choice and
 * must not survive into another session on a shared classroom device as though it were.
 */
let childLock = false;

/** Live state, so a sender does not pay for a `localStorage` read on every event. */
let current: TrackingConsent = DENIED;
let loaded = false;

type Listener = (consent: TrackingConsent) => void;
const listeners = new Set<Listener>();

/**
 * Read the stored decision.
 *
 * Every access is wrapped: a private window, cleared site data, or a blocked cookie policy makes
 * `localStorage` throw rather than return null, and an exception here would take down whatever
 * component is rendering. It falls back to DENIED, which is the safe direction.
 */
const readStored = (): TrackingConsent => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DENIED;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return DENIED;
    const record = parsed as Record<string, unknown>;
    // Strictly boolean-true, for S35's reason: a truthy string in storage must not grant.
    return {
      functional: record.functional === true,
      analytics: record.analytics === true,
      decided: record.decided === true,
    };
  } catch {
    return DENIED;
  }
};

const notify = (): void => {
  /*
   * The EFFECTIVE consent, not the stored record.
   *
   * These two differ exactly when `childLock` is set, and that is the case the listeners exist
   * for: a child signing in on a browser where an adult accepted. Passing `current` would hand
   * every subscriber `analytics: true` at the moment the product decided to send nothing — a
   * banner that hides itself while a Settings panel says telemetry is on. Caught by its own test
   * rather than in production, which is the only reason it is a comment and not a finding.
   */
  const effective = getTrackingConsent();
  for (const listener of listeners) {
    try {
      listener(effective);
    } catch (error) {
      // One bad listener must not stop the others being told a viewer withdrew consent.
      logger.error('A tracking-consent listener threw', error);
    }
  }
};

/** The current decision. Loads from storage once, then serves memory. */
export function getTrackingConsent(): TrackingConsent {
  if (!loaded) {
    current = readStored();
    loaded = true;
  }
  if (childLock) return { functional: false, analytics: false, decided: true };
  return current;
}

/** Is this category permitted right now? The question every sender asks. */
export function mayTrack(category: ConsentCategory): boolean {
  return getTrackingConsent()[category] === true;
}

/** Record the viewer's answer. Persisting is best-effort; honouring it is not. */
export function setTrackingConsent(choice: { functional: boolean; analytics: boolean }): void {
  if (childLock) {
    // Nothing to record: the account cannot grant this, and writing it down would make a
    // later `getTrackingConsent()` on an unlocked session read as a grant.
    logger.debug('Tracking consent not recorded: this account cannot give it');
    return;
  }
  current = { functional: choice.functional === true, analytics: choice.analytics === true, decided: true };
  loaded = true;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
  } catch {
    /*
     * The decision still applies for this session — `current` holds it and every sender reads
     * it from there. A viewer in a private window who refuses is refused for as long as the tab
     * lives, which is as long as there is anything to refuse. Failing to persist must not be
     * allowed to fail OPEN.
     */
    logger.debug('Tracking consent could not be stored; honoured for this session only');
  }
  notify();
}

/**
 * Forget the decision, so the banner asks again.
 *
 * Exists because "withdraw consent" has to be as easy as giving it, and a Settings control
 * needs something to call. It leaves the categories DENIED, not merely unanswered.
 */
export function resetTrackingConsent(): void {
  current = DENIED;
  loaded = true;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to remove, or storage is unavailable; the in-memory state is already DENIED */
  }
  notify();
}

/**
 * This account is a COPPA-covered child: nothing non-essential, and no question asked.
 *
 * Called from the auth context when a profile is read. Idempotent, and it notifies, because a
 * child signing in on a browser where an adult accepted must stop sending immediately rather
 * than at the next page load.
 */
export function lockForChildAccount(): void {
  if (childLock) return;
  childLock = true;
  notify();
}

/** The account signed out, or is not covered. Restores the viewer's own stored decision. */
export function unlockForAdultAccount(): void {
  if (!childLock) return;
  childLock = false;
  notify();
}

/** Whether the choice is unavailable because a child holds the session. */
export function isConsentLocked(): boolean {
  return childLock;
}

/** Subscribe to changes. Returns an unsubscribe function. */
export function onTrackingConsentChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test seam: forget everything this module has cached. */
export function __resetTrackingConsentForTests(): void {
  current = DENIED;
  loaded = false;
  childLock = false;
  listeners.clear();
}
